/**
 * Reusable SmartImprove admin config page.
 *
 * Drop-in surface for every library entity (operational areas, issue library,
 * KPI library, insight templates, recommendation templates, ROI models,
 * implementation templates, assignment rules, playbook, AI guidance). Each
 * wrapper supplies:
 *   - `title` / `description` / `entityLabel`
 *   - `fields` — render config for the create/edit dialog
 *   - `columns` — render config for the table
 *   - `api` — { list, create, update, remove }
 *   - `emptyRow` — initial values for the form
 *
 * Visual language is intentionally identical to the existing SmartEvaluate
 * admin pages (RecommendationsPage, TriggersPage, etc.) so the SmartImprove
 * section feels native beside SmartEvaluate.
 */
import { useState, useEffect, useMemo, useCallback } from "react";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../../components/ui/dialog";
import { Plus, Pencil, Trash2, Check, X } from "lucide-react";

const SEVERITY_PILL = {
  low: "bg-[#F1F5F9] text-[#475569] border-[#E2E8F0]",
  medium: "bg-[#EFF6FF] text-[#1D4ED8] border-[#BFDBFE]",
  high: "bg-[#FFF7ED] text-[#C2410C] border-[#FED7AA]",
  critical: "bg-[#FEF2F2] text-[#B91C1C] border-[#FECACA]",
};

function asInputValue(v) {
  if (v === null || v === undefined) return "";
  if (Array.isArray(v)) return v.join(", ");
  return String(v);
}

function parseField(field, raw) {
  const v = raw === undefined ? "" : raw;
  switch (field.type) {
    case "number":
      if (v === "" || v === null) return null;
      return Number(v);
    case "boolean":
      return Boolean(v);
    case "list":
      return String(v || "")
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
    default:
      return typeof v === "string" ? v : v ?? "";
  }
}

function renderCell(field, value) {
  if (value === null || value === undefined || value === "") return <span className="text-[#9CA3AF]">—</span>;
  if (field.type === "list" || Array.isArray(value)) {
    const arr = Array.isArray(value) ? value : [];
    if (!arr.length) return <span className="text-[#9CA3AF]">—</span>;
    return (
      <div className="flex flex-wrap gap-1">
        {arr.slice(0, 4).map((t, i) => (
          <span
            key={i}
            className="px-1.5 py-0.5 text-[10px] rounded-sm border border-[#E5E7EB] bg-[#F8FAFC] text-[#374151]"
          >
            {t}
          </span>
        ))}
        {arr.length > 4 ? (
          <span className="text-[10px] text-[#6B7280]">+{arr.length - 4}</span>
        ) : null}
      </div>
    );
  }
  if (field.type === "severity") {
    const s = String(value).toLowerCase();
    return (
      <span
        className={`inline-flex items-center px-2 py-0.5 text-[11px] font-semibold border rounded-sm capitalize ${
          SEVERITY_PILL[s] || "bg-[#F3F4F6] text-[#374151] border-[#E5E7EB]"
        }`}
      >
        {s}
      </span>
    );
  }
  if (field.type === "boolean") {
    return value ? <Check className="w-3.5 h-3.5 text-[#0EA56F]" /> : <X className="w-3.5 h-3.5 text-[#9CA3AF]" />;
  }
  return <span className="text-[#374151]">{String(value)}</span>;
}

function emptyFromFields(fields) {
  const row = {};
  fields.forEach((f) => {
    if (f.type === "boolean") row[f.key] = f.default ?? true;
    else if (f.type === "list") row[f.key] = [];
    else if (f.type === "number") row[f.key] = f.default ?? null;
    else row[f.key] = f.default ?? "";
  });
  if (!("active" in row)) row.active = true;
  if (!("sort_order" in row)) row.sort_order = 0;
  return row;
}

export default function SmartImproveConfigPage({
  title,
  description,
  entityLabel,
  testId,
  fields,
  columns,
  api,
  rowKey = "id",
  allowDelete = true,
}) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null); // row or null (create)
  const [form, setForm] = useState(() => emptyFromFields(fields));
  const [saving, setSaving] = useState(false);

  const reload = useCallback(() => {
    setLoading(true);
    return api
      .list()
      .then((data) => setRows(data || []))
      .catch(() => toast.error(`Failed to load ${entityLabel}`))
      .finally(() => setLoading(false));
  }, [api, entityLabel]);

  useEffect(() => {
    reload();
  }, [reload]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyFromFields(fields));
    setOpen(true);
  };
  const openEdit = (row) => {
    setEditing(row);
    const f = {};
    fields.forEach((field) => {
      const raw = row[field.key];
      if (field.type === "list") f[field.key] = Array.isArray(raw) ? raw : [];
      else if (field.type === "boolean") f[field.key] = raw !== false;
      else if (field.type === "number") f[field.key] = raw ?? null;
      else f[field.key] = raw ?? "";
    });
    f.active = row.active !== false;
    f.sort_order = row.sort_order ?? 0;
    setForm(f);
    setOpen(true);
  };

  const save = async () => {
    // Build payload through field parsers so types are correct on the wire.
    const payload = { active: !!form.active, sort_order: Number(form.sort_order) || 0 };
    for (const field of fields) {
      payload[field.key] = parseField(field, form[field.key]);
    }
    setSaving(true);
    try {
      if (editing) await api.update(editing.id, payload);
      else await api.create(payload);
      toast.success(`${entityLabel} ${editing ? "updated" : "created"}`);
      setOpen(false);
      await reload();
    } catch (e) {
      const detail = e?.response?.data?.detail || `Failed to save ${entityLabel}`;
      toast.error(typeof detail === "string" ? detail : `Failed to save ${entityLabel}`);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete this ${entityLabel}?`)) return;
    try {
      await api.remove(row.id);
      toast.success(`${entityLabel} deleted`);
      await reload();
    } catch (e) {
      toast.error(`Failed to delete ${entityLabel}`);
    }
  };

  const columnsResolved = useMemo(
    () =>
      columns ||
      fields.slice(0, 4).map((f) => ({ key: f.key, label: f.label, type: f.type })),
    [columns, fields]
  );

  return (
    <div className="space-y-5" data-testid={testId}>
      <header className="flex items-start justify-between">
        <div>
          <p className="text-[11px] font-semibold tracking-[0.18em] uppercase text-[#025979]">
            SmartImprove · Configuration
          </p>
          <h1
            className="text-2xl font-bold text-[#111827] mt-1"
            style={{ fontFamily: "Chivo, sans-serif" }}
          >
            {title}
          </h1>
          {description ? (
            <p className="text-sm text-[#6B7280] mt-1 max-w-2xl">{description}</p>
          ) : null}
        </div>
        <Button
          onClick={openCreate}
          className="bg-[#025979] hover:bg-[#03738C] text-white rounded-sm text-xs"
          data-testid={`${testId}-add-btn`}
        >
          <Plus className="w-3.5 h-3.5 mr-1.5" /> Add {entityLabel}
        </Button>
      </header>

      <section className="bg-white border border-[#E5E7EB] rounded-sm">
        {loading ? (
          <div className="px-5 py-8 text-sm text-[#6B7280]">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="px-5 py-12 text-center text-sm text-[#6B7280]" data-testid={`${testId}-empty`}>
            No entries yet. Click <strong>Add {entityLabel}</strong> to create the first one.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-[#F8FAFC] text-left text-[11px] uppercase tracking-wider text-[#6B7280]">
              <tr>
                {columnsResolved.map((c) => (
                  <th key={c.key} className="px-4 py-2 font-semibold">
                    {c.label}
                  </th>
                ))}
                <th className="px-4 py-2 font-semibold text-right">Active</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr
                  key={row[rowKey]}
                  className="border-t border-[#F1F5F9] hover:bg-[#F8FAFC]"
                  data-testid={`${testId}-row-${row[rowKey]}`}
                >
                  {columnsResolved.map((c) => (
                    <td key={c.key} className="px-4 py-3 align-top">
                      {renderCell({ type: c.type }, row[c.key])}
                    </td>
                  ))}
                  <td className="px-4 py-3 text-right">
                    {row.active === false ? (
                      <span className="inline-flex items-center px-2 py-0.5 text-[11px] font-semibold border rounded-sm bg-[#F3F4F6] text-[#6B7280] border-[#E5E7EB]">
                        inactive
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2 py-0.5 text-[11px] font-semibold border rounded-sm bg-[#ECFDF5] text-[#065F46] border-[#A7F3D0]">
                        active
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-3 text-right whitespace-nowrap">
                    <button
                      onClick={() => openEdit(row)}
                      className="text-xs font-semibold text-[#025979] hover:text-[#03738C] mr-3"
                      data-testid={`${testId}-edit-${row[rowKey]}`}
                    >
                      <Pencil className="inline w-3.5 h-3.5" />
                    </button>
                    {allowDelete ? (
                      <button
                        onClick={() => remove(row)}
                        className="text-xs font-semibold text-[#B91C1C] hover:text-[#7F1D1D]"
                        data-testid={`${testId}-delete-${row[rowKey]}`}
                      >
                        <Trash2 className="inline w-3.5 h-3.5" />
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl" data-testid={`${testId}-dialog`}>
          <DialogHeader>
            <DialogTitle>
              {editing ? `Edit ${entityLabel}` : `New ${entityLabel}`}
            </DialogTitle>
            <DialogDescription>
              All edits are saved instantly and are versioned in the audit log.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-2 max-h-[60vh] overflow-y-auto pr-1">
            {fields.map((field) => (
              <FieldEditor
                key={field.key}
                field={field}
                value={form[field.key]}
                onChange={(v) => setForm((s) => ({ ...s, [field.key]: v }))}
                testId={`${testId}-field-${field.key}`}
              />
            ))}
            <div className="flex items-center gap-2">
              <input
                id={`${testId}-active`}
                type="checkbox"
                checked={!!form.active}
                onChange={(e) => setForm((s) => ({ ...s, active: e.target.checked }))}
                data-testid={`${testId}-field-active`}
              />
              <label htmlFor={`${testId}-active`} className="text-xs text-[#374151]">
                Active
              </label>
            </div>
            <div>
              <label className="block text-xs font-medium text-[#374151] mb-1">
                Sort order
              </label>
              <Input
                type="number"
                value={form.sort_order ?? 0}
                onChange={(e) =>
                  setForm((s) => ({ ...s, sort_order: e.target.value }))
                }
                className="rounded-sm"
                data-testid={`${testId}-field-sort_order`}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setOpen(false)}
              className="rounded-sm text-xs"
              data-testid={`${testId}-cancel-btn`}
            >
              Cancel
            </Button>
            <Button
              onClick={save}
              disabled={saving}
              className="bg-[#025979] hover:bg-[#03738C] text-white rounded-sm text-xs"
              data-testid={`${testId}-save-btn`}
            >
              {saving ? "Saving…" : editing ? "Save" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function FieldEditor({ field, value, onChange, testId }) {
  const span = field.span === "full" ? "col-span-2" : "col-span-1";
  const label = (
    <label className="block text-xs font-medium text-[#374151] mb-1">
      {field.label}
      {field.required ? <span className="text-red-500">*</span> : null}
    </label>
  );
  if (field.type === "select") {
    return (
      <div className={span}>
        {label}
        <Select
          value={asInputValue(value)}
          onValueChange={(v) => onChange(v)}
        >
          <SelectTrigger className="rounded-sm text-xs" data-testid={testId}>
            <SelectValue placeholder={field.placeholder || "Select…"} />
          </SelectTrigger>
          <SelectContent>
            {(field.options || []).map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    );
  }
  if (field.type === "severity") {
    return (
      <div className={span}>
        {label}
        <Select value={asInputValue(value)} onValueChange={(v) => onChange(v)}>
          <SelectTrigger className="rounded-sm text-xs" data-testid={testId}>
            <SelectValue placeholder="Select severity" />
          </SelectTrigger>
          <SelectContent>
            {["low", "medium", "high", "critical"].map((s) => (
              <SelectItem key={s} value={s} className="capitalize">
                {s}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    );
  }
  if (field.type === "textarea") {
    return (
      <div className={span}>
        {label}
        <Textarea
          value={asInputValue(value)}
          onChange={(e) => onChange(e.target.value)}
          rows={field.rows || 3}
          className="rounded-sm text-xs"
          placeholder={field.placeholder}
          data-testid={testId}
        />
      </div>
    );
  }
  if (field.type === "number") {
    return (
      <div className={span}>
        {label}
        <Input
          type="number"
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))}
          className="rounded-sm text-xs"
          placeholder={field.placeholder}
          data-testid={testId}
        />
      </div>
    );
  }
  if (field.type === "list") {
    return (
      <div className={span}>
        {label}
        <Input
          value={asInputValue(value)}
          onChange={(e) =>
            onChange(
              e.target.value
                .split(",")
                .map((s) => s.trim())
                .filter(Boolean)
            )
          }
          className="rounded-sm text-xs"
          placeholder={field.placeholder || "comma,separated,values"}
          data-testid={testId}
        />
      </div>
    );
  }
  return (
    <div className={span}>
      {label}
      <Input
        value={asInputValue(value)}
        onChange={(e) => onChange(e.target.value)}
        className="rounded-sm text-xs"
        placeholder={field.placeholder}
        data-testid={testId}
      />
    </div>
  );
}
